// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Sex, StateCode, SeriesKey } from './types';

/** Sex colours — used across most views. Distinct without garish pink/blue. */
export const SEX_COLOUR: Record<Sex, string> = { boy: '#2e7d9a', girl: '#c9527e' };
export const SEX_LABEL: Record<Sex, string> = { boy: 'Boys', girl: 'Girls' };

/** State colours — a nod to the states' sporting colours (NSW blue, QLD maroon).
 *  Only ever plotted alongside each other (never with the sex colours). */
export const STATE_COLOUR: Record<StateCode, string> = { NSW: '#3b6fb0', QLD: '#8a2f52' };
export const STATE_LABEL: Record<StateCode, string> = { NSW: 'New South Wales', QLD: 'Queensland' };

export const SERIES_COLOUR: Record<SeriesKey, string> = {
  'NSW-boy': '#2e7d9a',
  'NSW-girl': '#c9527e',
  'QLD-boy': '#3f9d8a',
  'QLD-girl': '#b3603f',
};

export function seriesLabel(sk: SeriesKey): string {
  const [st, sx] = sk.split('-') as [StateCode, Sex];
  return `${st} ${SEX_LABEL[sx].toLowerCase()}`;
}

/** A warm sequential ramp (cream → terracotta) for heatmaps. Stops chosen so the
 *  low end is a visible cream, not near-white. */
export const WARM_RAMP = ['#fbf1e0', '#f6dcb6', '#efbf86', '#e59f5c', '#d67c3e', '#bf5a2c', '#9c3c22'];

export function rampColour(t: number): string {
  if (!Number.isFinite(t)) return '#efe6d6';
  const clamped = Math.max(0, Math.min(1, t));
  const idx = Math.min(WARM_RAMP.length - 1, Math.floor(clamped * WARM_RAMP.length));
  return WARM_RAMP[idx];
}

/** Diverging ramp for signed values (rank falls ↔ rank rises). */
export const DIVERGE = { up: '#3f8f4f', down: '#c0492f', neutral: '#c3b8a6' };
